/**
 * PDP Algolia Insights Tracking (Hey Dude React Implementation)
 *
 * Handles URL parameter extraction and cart form enhancement for Algolia tracking.
 * Adapted from Ted Baker's Vue implementation for Hey Dude's React/TypeScript setup.
 *
 * Usage in product liquid templates:
 * <script src="{{ 'pdp-algolia-tracking.js' | asset_url }}" defer></script>
 * <script>
 *   document.addEventListener('DOMContentLoaded', () => {
 *     if (window.PDPAlgoliaTracking) {
 *       window.PDPAlgoliaTracking.init('{{ product.id }}');
 *     }
 *   });
 * </script>
 */

(function() {
  'use strict';

  let algoliaContext = null;
  let productId = null;

  /**
   * Extract Algolia tracking parameters from URL
   * Expected params: qid (queryID), pos (position)
   */
  function extractAlgoliaParams() {
    const params = new URLSearchParams(window.location.search);
    const queryID = params.get('qid');
    const position = params.get('pos');

    if (queryID) {
      return {
        queryID,
        position: position ? parseInt(position) : null,
        timestamp: Date.now()
      };
    }
    return null;
  }

  /**
   * Add hidden inputs to cart forms for Algolia tracking
   */
  function enhanceCartForms(context) {
    const forms = document.querySelectorAll('form[action*="/cart/add"]');

    forms.forEach(form => {
      // Check if already enhanced to avoid duplicates
      if (form.querySelector('[name="properties[_algolia_query_id]"]')) {
        return;
      }

      // Add Algolia tracking properties as hidden inputs
      const hiddenInputs = `
        <input type="hidden" name="properties[_algolia_query_id]" value="${context.queryID}">
        <input type="hidden" name="properties[_algolia_position]" value="${context.position || ''}">
        <input type="hidden" name="properties[_algolia_product_id]" value="${productId}">
        <input type="hidden" name="properties[_algolia_timestamp]" value="${context.timestamp}">
      `;

      form.insertAdjacentHTML('beforeend', hiddenInputs);
      console.log('[PDP Tracking] Enhanced cart form with Algolia properties');
    });
  }

  /**
   * Track add to cart event immediately using React insights module
   */
  function trackAddToCart(productObjectId, variantId = null) {
    if (!algoliaContext || !window.AlgoliaInsights) {
      console.warn('[PDP Tracking] Missing context or insights client');
      return;
    }

    try {
      // Use the React insights module if available
      if (window.AlgoliaInsights && window.AlgoliaInsights.trackAddToCart) {
        window.AlgoliaInsights.trackAddToCart(productObjectId, algoliaContext.queryID);
      }

      console.log('[PDP Tracking] Add to cart tracked:', {
        productObjectId,
        variantId,
        queryID: algoliaContext.queryID,
        position: algoliaContext.position
      });
    } catch (error) {
      console.error('[PDP Tracking] Failed to track add to cart:', error);
    }
  }

  /**
   * Track product page view if coming from search
   */
  function trackProductPageView() {
    if (!algoliaContext || !window.AlgoliaInsights) {
      return;
    }

    try {
      if (window.AlgoliaInsights && window.AlgoliaInsights.trackProductPageView) {
        window.AlgoliaInsights.trackProductPageView(productId, 'shopify_products', algoliaContext.queryID);
      }

      console.log('[PDP Tracking] Product page view tracked:', {
        productId,
        queryID: algoliaContext.queryID,
        position: algoliaContext.position
      });
    } catch (error) {
      console.error('[PDP Tracking] Failed to track page view:', error);
    }
  }

  /**
   * Setup event listeners for cart form submissions
   */
  function setupCartFormTracking() {
    // Monitor for cart form submissions
    document.addEventListener('submit', function(event) {
      const form = event.target;

      // Check if it's a cart add form
      if (form.matches('form[action*="/cart/add"]')) {
        const formData = new FormData(form);
        const variantId = formData.get('id');

        // Track the add to cart event
        trackAddToCart(productId, variantId);
      }
    });

    // Monitor for React/modern cart component submissions
    document.addEventListener('click', function(event) {
      const button = event.target.closest('[data-test="add-to-cart-button"], [type="submit"][form*="cart"], .add-to-cart-button');

      if (button) {
        // Small delay to ensure form data is processed
        setTimeout(() => {
          trackAddToCart(productId);
        }, 100);
      }
    });
  }

  /**
   * Watch for dynamically added cart forms (for React components)
   */
  function setupDynamicFormWatcher() {
    if (!window.MutationObserver) {
      return;
    }

    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) { // Element node
            // Check if the node itself is a cart form
            const forms = node.matches && node.matches('form[action*="/cart/add"]') ? [node] : [];
            // Check for nested cart forms
            const nestedForms = node.querySelectorAll ? node.querySelectorAll('form[action*="/cart/add"]') : [];

            [...forms, ...nestedForms].forEach(form => {
              if (algoliaContext) {
                enhanceCartForms(algoliaContext);
              }
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    console.log('[PDP Tracking] Dynamic form watcher initialized');
  }

  /**
   * Initialize PDP Algolia tracking
   * @param {string} prodId - Shopify product ID
   */
  function init(prodId) {
    productId = prodId;
    algoliaContext = extractAlgoliaParams();

    if (algoliaContext) {
      console.log('[PDP Tracking] Initialized with context:', algoliaContext);

      // Track product page view from search
      trackProductPageView();

      // Enhance existing cart forms
      enhanceCartForms(algoliaContext);

      // Setup event listeners
      setupCartFormTracking();

      // Watch for dynamically added forms
      setupDynamicFormWatcher();

    } else {
      console.log('[PDP Tracking] No Algolia tracking params found in URL');
    }
  }

  /**
   * Public API
   */
  window.PDPAlgoliaTracking = {
    init: init,
    getContext: () => algoliaContext,
    trackAddToCart: trackAddToCart,
    trackProductPageView: trackProductPageView
  };

})();